
import { isEmpty } from 'radash';
import { create } from 'zustand';

import { Validation } from '../types';

enum ValidationStatus {
    IS_NOT_ASSIGNED = 'NA',
    IS_DIRTY = 'isDirty',
    IS_COMPLIANT = 'isCompliant',
    IS_NOT_COMPLIANT = 'isNotCompliant',
    IS_RUNNING = 'isRunning',
    IS_FAILED = 'isFailed'
}

interface ValidationsStore {
    status: ValidationStatus;
    validationErrors: {
        [key: string]: Validation[];
    };
    validationWarnings: {
        [key: string]: Validation[];
    };
    // `localValidationErrors` stores client-only validation issues (UI/form logic) that are not returned by server validation.
    // For example, MultiReference and MultiDimensional attributes need to validate that referenced items are valid/available before allowing save.
    localValidationErrors: {
        [key: string]: Validation[];
    }
}

interface ServerValidationEntry {
    path: string;
    kind: 'error' | 'warning';
    validation: Validation;
}

interface ServerValidationOperations {
    clearAll: boolean;
    attributeNamesToReset: string[];
    entries: ServerValidationEntry[];
}

interface Actions {
    addError: (attributePath: string, validation: Validation) => void;
    removeErrorByPath: (attributePath: string) => void;
    removeErrorByAttributeName: (attributeName: string) => void;
    addWarning: (attributePath: string, validation: Validation) => void;
    removeWarningByPath: (attributePath: string) => void;
    removeWarningByAttributeName: (attributePath: string) => void;
    addLocalValidationError: (attributePath: string, validation: Validation) => void;
    removeLocalValidationError: (attributePath: string) => void;
    removeLocalValidationErrorByAttributeName: (attributeName: string) => void;
    clearErrors: () => void;
    clearWarnings: () => void;
    clearLocalValidationErrors: () => void;
    setStatus: (status: ValidationStatus) => void;
    // Apply a full server validation response in a single set() (LAX-9602)
    applyServerValidations: (operations: ServerValidationOperations) => void;
    // New optimized selector for multiple attributes
    getValidationsForAttributes: (attributeNames: string[], type: 'errors' | 'warnings') => Validation[];
}

const initialState: ValidationsStore = {
    status: ValidationStatus.IS_NOT_ASSIGNED,
    validationErrors: {},
    validationWarnings: {},
    localValidationErrors: {},
};

const filterOutAttributeNames = (
    validationsByPath: Record<string, Validation[]>,
    attributeNames: Set<string>,
): Record<string, Validation[]> => {
    const filtered: Record<string, Validation[]> = {};
    Object.keys(validationsByPath).forEach((path) => {
        const remaining = validationsByPath[path].filter(
            (validation) => !attributeNames.has(validation.attributeName),
        );
        if (remaining.length > 0) {
            filtered[path] = remaining;
        }
    });
    return filtered;
};

const appendValidation = (
    validationsByPath: Record<string, Validation[]>,
    path: string,
    validation: Validation,
) => {
    if (!isEmpty(validationsByPath[path])) {
        validationsByPath[path] = [
            ...validationsByPath[path] as Validation[],
            validation,
        ];
    } else {
        validationsByPath[path] = [validation];
    }
};

const useValidationsStore = create<ValidationsStore & Actions>((set, get) => ({
    ...initialState,
    addError: (attributePath, validation) => {
        set((state) => {
            if (!isEmpty(state.validationErrors[attributePath])) {
                return {
                    ...state,
                    validationErrors: {
                        ...state.validationErrors,
                        [attributePath]: [
                            ...state.validationErrors[attributePath] as Validation[],
                            validation
                        ]
                    }
                };
            } else {
                return {
                    ...state,
                    validationErrors: {
                        ...state.validationErrors,
                        [attributePath]: [
                            validation
                        ]
                    }
                };
            }
        });
    },
    removeErrorByPath: (attributePath) => {
        set((state) => {
            if (!isEmpty(state.validationErrors[attributePath])) {
                delete state.validationErrors[attributePath];
                return {
                    ...state,
                    validationErrors: {
                        ...state.validationErrors
                    }
                };
            }

            return state;
        });
    },
    // Remove all the validation errors that has the given attribute name, remember that attribute path is different from
    // attribute name.
    removeErrorByAttributeName: (attributeName) => {
        set((state) => {

            const filteredValidationErrors: Record<string, Validation[]> = {};
            const storedValidationPaths = Object.keys(state.validationErrors);
            storedValidationPaths.forEach(path => {
                const validationsByPath = state.validationErrors[path];

                const filteredValidations = validationsByPath.filter(validation => validation.attributeName !== attributeName);

                if (filteredValidations.length > 0) {
                    filteredValidationErrors[path] = filteredValidations;
                }
            });

            return {
                ...state,
                validationErrors: filteredValidationErrors
            };
        });
    },
    addWarning: (attributePath, validation) => {
        set((state) => {
            if (!isEmpty(state.validationWarnings[attributePath])) {
                return {
                    ...state,
                    validationWarnings: {
                        ...state.validationWarnings,
                        [attributePath]: [
                            ...state.validationWarnings[attributePath] as Validation[],
                            validation
                        ]
                    }
                };
            } else {
                return {
                    ...state,
                    validationWarnings: {
                        ...state.validationWarnings,
                        [attributePath]: [
                            validation
                        ]
                    }
                };
            }
        });
    },
    removeWarningByPath: (attributePath) => {
        set((state) => {
            if (!isEmpty(state.validationWarnings[attributePath])) {
                delete state.validationWarnings[attributePath];
                return {
                    ...state,
                    validationWarnings: {
                        ...state.validationWarnings
                    }
                };
            }

            return state;
        });
    },
    removeWarningByAttributeName: (attributeName) => {
        set((state) => {

            const filteredValidationWarnings: Record<string, Validation[]> = {};
            const storedValidationPaths = Object.keys(state.validationWarnings);
            storedValidationPaths.forEach(path => {
                const validationsByPath = state.validationWarnings[path];

                const filteredValidations = validationsByPath.filter(validation => validation.attributeName !== attributeName);
                // Only include paths that still have validations after filtering
                // This ensures paths are completely removed when all warnings are cleared
                if (filteredValidations.length > 0) {
                    filteredValidationWarnings[path] = filteredValidations;
                }
            });

            return {
                ...state,
                validationWarnings: filteredValidationWarnings
            };
        });
    },
    addLocalValidationError: (attributePath, validation) => {
        set((state) => {
            if (!isEmpty(state.localValidationErrors[attributePath])) {
                return {
                    ...state,
                    localValidationErrors: {
                        ...state.localValidationErrors,
                        [attributePath]: [
                            ...state.localValidationErrors[attributePath] as Validation[],
                            validation
                        ]
                    }
                };
            } else {
                return {
                    ...state,
                    localValidationErrors: {
                        ...state.localValidationErrors,
                        [attributePath]: [
                            validation
                        ]
                    }
                };
            }
        });
    },
    removeLocalValidationError: (attributePath) => {
        set((state) => {
            if (!isEmpty(state.localValidationErrors[attributePath])) {
                delete state.localValidationErrors[attributePath];
                return {
                    ...state,
                    localValidationErrors: {
                        ...state.localValidationErrors
                    }
                };
            }

            return state;
        });
    },
    // Remove all the local validation errors that have the given attribute name
    removeLocalValidationErrorByAttributeName: (attributeName) => {
        set((state) => {
            const filteredLocalValidationErrors: Record<string, Validation[]> = {};
            const storedValidationPaths = Object.keys(state.localValidationErrors);
            storedValidationPaths.forEach(path => {
                const validationsByPath = state.localValidationErrors[path];

                const filteredValidations = validationsByPath.filter(validation => validation.attributeName !== attributeName);

                if (filteredValidations.length > 0) {
                    filteredLocalValidationErrors[path] = filteredValidations;
                }
            });

            return {
                ...state,
                localValidationErrors: filteredLocalValidationErrors
            };
        });
    },
    clearErrors: () => {
        set((state) => ({
            ...state,
            validationErrors: {}
        }));
    },
    clearWarnings: () => {
        set((state) => ({
            ...state,
            validationWarnings: {}
        }));
    },
    clearLocalValidationErrors: () => {
        set((state) => ({
            ...state,
            localValidationErrors: {}
        }));
    },
    setStatus: (status) => {
        set((state) => ({
            ...state,
            status
        }));
    },
    applyServerValidations: ({ clearAll, attributeNamesToReset, entries }) => {
        if (!clearAll && attributeNamesToReset.length === 0 && entries.length === 0) {
            return;
        }

        set((state) => {
            const namesToReset = new Set(attributeNamesToReset);
            const validationErrors = clearAll
                ? {}
                : filterOutAttributeNames(state.validationErrors, namesToReset);
            const validationWarnings = clearAll
                ? {}
                : filterOutAttributeNames(state.validationWarnings, namesToReset);

            entries.forEach(({ path, kind, validation }) => {
                if (kind === 'error') {
                    appendValidation(validationErrors, path, validation);
                } else {
                    appendValidation(validationWarnings, path, validation);
                }
            });

            return {
                ...state,
                validationErrors,
                validationWarnings,
            };
        });
    },
    // New optimized selector for multiple attributes
    getValidationsForAttributes: (attributeNames, type) => {
        const state = get();
        const validationKey = type === 'errors' ? 'validationErrors' : 'validationWarnings';
        const validations: Validation[] = [];
        const seenValidations = new Set<string>();

        const getValidationKey = (validation: Validation) => {
            const pathStr = JSON.stringify(validation.path);
            return `${validation.attributeName}:${validation.name}:${pathStr}`;
        };

        Object.keys(state[validationKey]).forEach(path => {
            const pathValidations = state[validationKey][path];
            if (pathValidations) {
                pathValidations.forEach(validation => {
                    if (attributeNames.includes(validation.attributeName)) {
                        const key = getValidationKey(validation);
                        if (!seenValidations.has(key)) {
                            seenValidations.add(key);
                            validations.push(validation);
                        }
                    }
                });
            }
        });

        if (type === 'errors') {
            Object.keys(state.localValidationErrors).forEach(path => {
                const pathValidations = state.localValidationErrors[path];
                if (pathValidations) {
                    pathValidations.forEach(validation => {
                        if (attributeNames.includes(validation.attributeName)) {
                            const key = getValidationKey(validation);
                            if (!seenValidations.has(key)) {
                                seenValidations.add(key);
                                validations.push(validation);
                            }
                        }
                    });
                }
            });
        }

        return validations;
    }
}));

export {
    useValidationsStore,
    type Validation,
    ValidationStatus,
    type ServerValidationOperations,
};

